import React from 'react';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, AlertCircle, RotateCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FailedUpload {
  fileName: string;
  fileSize: number;
  error: string;
  timestamp: number;
  partialVariants: string[];
  attempts: number;
}

interface PhotoUploadProgressProps {
  croppingProgress: { current: number; total: number };
  uploadProgress: { current: number; total: number };
  failedUploads?: FailedUpload[];
  onRetryUpload?: (upload: FailedUpload) => void;
  onDiscardUpload?: (upload: FailedUpload) => void;
}

const PhotoUploadProgress: React.FC<PhotoUploadProgressProps> = ({
  croppingProgress,
  uploadProgress,
  failedUploads = [],
  onRetryUpload,
  onDiscardUpload
}) => {
  const croppingPercent = croppingProgress.total > 0 
    ? (croppingProgress.current / croppingProgress.total) * 100 
    : 0;
  
  const uploadPercent = uploadProgress.total > 0 
    ? (uploadProgress.current / uploadProgress.total) * 100 
    : 0;

  const croppingComplete = croppingProgress.current === croppingProgress.total && croppingProgress.total > 0;
  const uploadComplete = uploadProgress.current === uploadProgress.total && uploadProgress.total > 0;

  // Only show if there's actual progress or failed uploads to display
  if (croppingProgress.total === 0 && uploadProgress.total === 0 && failedUploads.length === 0) {
    return null;
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 shadow-xl backdrop-blur-sm">
      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
        📸 Progresso de Upload
      </h3>
      
      {/* Cropping Progress */}
      {croppingProgress.total > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-white/80 text-sm">🖼️ Ajustando</span>
              {croppingComplete && (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              )}
            </div>
            <span className="text-white/60 text-xs font-mono">
              {croppingProgress.current}/{croppingProgress.total}
            </span>
          </div>
          <Progress 
            value={croppingPercent} 
            indicatorClassName="bg-gradient-to-r from-luxury-gold to-yellow-500 transition-all duration-500"
            className="h-2 bg-white/10"
          />
        </div>
      )}
      
      {/* Upload Progress */}
      {uploadProgress.total > 0 && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-white/80 text-sm">📤 Enviando</span>
              {uploadComplete && (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              )}
            </div>
            <span className="text-white/60 text-xs font-mono">
              {uploadProgress.current}/{uploadProgress.total}
            </span>
          </div>
          <Progress 
            value={uploadPercent} 
            indicatorClassName="bg-gradient-to-r from-luxury-gold to-yellow-500 transition-all duration-500"
            className="h-2 bg-white/10"
          />
        </div>
      )}

      {/* Success message when all complete */}
      {croppingComplete && uploadComplete && failedUploads.length === 0 && (
        <p className="text-green-500 text-sm text-center mt-4 flex items-center justify-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Todas as fotos foram enviadas com sucesso!
        </p>
      )}

      {/* Failed uploads section */}
      {failedUploads.length > 0 && (
        <div className="mt-4 pt-4 border-t border-red-500/20">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm font-semibold">
              {failedUploads.length} {failedUploads.length === 1 ? 'foto falhou' : 'fotos falharam'}
            </span>
          </div>
          
          <div className="space-y-2">
            {failedUploads.map((upload) => (
              <div 
                key={upload.timestamp}
                className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-xs font-medium truncate">
                      {upload.fileName}
                    </p>
                    <p className="text-red-400/80 text-xs mt-1">
                      {upload.error}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {onRetryUpload && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRetryUpload(upload)}
                        className="h-7 px-2 text-xs text-white/70 hover:text-white hover:bg-white/10"
                      >
                        <RotateCw className="w-3 h-3 mr-1" />
                        Tentar
                      </Button>
                    )}
                    {onDiscardUpload && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDiscardUpload(upload)}
                        className="h-7 px-2 text-xs text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUploadProgress;
